import PropTypes from "prop-types";
import {useRef} from "react";
import {useTranslation} from "react-i18next";
import {useNavigate} from "react-router-dom";
import GamanzaWidgetSlot from "@/components/GamanzaWidgetSlot";
import useGamanzaWidgetHydration from "@/hooks/useGamanzaWidgetHydration";
import useNotificationsFeed from "../../../hooks/notifications/useNotificationsFeed";
import useIsMobile from "../../../hooks/useIsMobile";
import usePagoMovilRegistration from "../../../hooks/usePagoMovilRegistration";
import {trackDepositButtonMenuClick} from "../../../providers/analytics";
import {useChangePassword} from "../../../store/useAccountScreens.store";
import {useAuthStore} from "../../../store/useAuth.store";
import {useBalanceStore} from "../../../store/useBalance.store";
import {useUserSupport} from "../../../store/useUserSupport.store";
import {useIsGamanzaUiEnabled} from "../../../utils/gamanzaUi";
import LanguageOption from "../../LanguageOption";
import {openNotificationDrawer} from "../../notification/notificationEvents";
import ThemeToggle from "../../ThemeToggle/ThemeToggle";
import styles from "./DropdownRender.module.css";

const MOBILE_BREAKPOINT = 799;
const DROPDOWN_POINTS_ID = "gamanza-dropdown-points";

const DropdownRender = ({handleLogout, dropdownStyle, handleClose}) => {
  const navigate = useNavigate();
  const {t} = useTranslation();
  const isMobile = useIsMobile({breakpoint: MOBILE_BREAKPOINT});
  const {unreadCount} = useNotificationsFeed();
  const gamanzaUiEnabled = useIsGamanzaUiEnabled();
  const authStatus = useAuthStore(state => state.authStatus);
  const token = useAuthStore(state => state.token);
  const pointsRef = useRef(null);

  const pointsEnabled = gamanzaUiEnabled && authStatus === "authenticated" && Boolean(token);
  const {isHydrating} = useGamanzaWidgetHydration({
    enabled: pointsEnabled,
    token,
    rootRef: pointsRef,
    widgetId: DROPDOWN_POINTS_ID,
  });

  const close = () => {
    if (typeof handleClose === "function") {
      handleClose();
    }
  };

  const handleChangePasswordModal = useChangePassword.getState().handleChangePasswordModal;
  const handleBankAccountModal = useBalanceStore.getState().handleBankAccountModal;
  const handleWithdrawalModal = useBalanceStore.getState().handleWithdrawalModal;
  const handlePagoMovilListModal = useBalanceStore.getState().handlePagoMovilListModal;
  const handleSupportModal = useUserSupport.getState().handleSupportModal;

  const {triggerRegistrationModalIfNeeded} = usePagoMovilRegistration();

  const handleDepositAction = async () => {
    trackDepositButtonMenuClick();

    const modalTriggered = await triggerRegistrationModalIfNeeded("deposit", true);

    if (!modalTriggered) {
      handleBankAccountModal();
      close();
    }
  };

  const handleWithdrawalAction = async () => {
    const modalTriggered = await triggerRegistrationModalIfNeeded("withdrawal", true);

    if (!modalTriggered) {
      handleWithdrawalModal();
      close();
    }
  };

  return (
    <div
      style={{
        backgroundColor: "var(--bg-main)",
        border: "none",
        color: "var(--text-primary)",
        padding: "2px 0 0 0",
        boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
        borderRadius: "6px",
        width: dropdownStyle?.width || undefined,
      }}
    >
      <ul style={{listStyle: "none", padding: 0}}>
        {pointsEnabled && (
          <li
            className={styles.pointsSlot}
            ref={pointsRef}
            aria-busy={isHydrating}
          >
            <GamanzaWidgetSlot
              id={DROPDOWN_POINTS_ID}
              dataType="points"
              className={styles.pointsWidget}
              isHydrating={isHydrating}
              variant="block"
              minHeight={160}
              dataAttrs={{
                "data-progress-type": "level",
              }}
            />
          </li>
        )}
        {isMobile && (
          <li
            className={styles.dropdown_menu}
            onClick={() => {
              openNotificationDrawer();
              close();
            }}
          >
            <span className={styles.dropdown_menuRow}>
              <span>{t("notificationDrawerTitle")}</span>
              {unreadCount > 0 && (
                <span className={styles.dropdownNotifBadge}>{unreadCount > 99 ? "99+" : unreadCount}</span>
              )}
            </span>
          </li>
        )}
        <li
          className={styles.dropdown_menu}
          onClick={() => {
            navigate("/settings");
            close();
          }}
        >
          {t("profile")}
        </li>
        <li
          className={styles.dropdown_menu}
          onClick={() => {
            handlePagoMovilListModal();
            close();
          }}
        >
          {t("mobilePaidAccounts")}
        </li>
        <li
          className={styles.dropdown_menu}
          onClick={() => {
            navigate("/user/transactions");
            close();
          }}
        >
          {t("movements")}
        </li>
        <li
          className={styles.dropdown_menu}
          onClick={() => {
            navigate("/user/contacts");
            close();
          }}
        >
          {t("contact")}
        </li>
        <li
          className={styles.dropdown_menu}
          onClick={() => {
            handleChangePasswordModal();
            close();
          }}
        >
          {t("changePassword")}
        </li>
        <li
          className={styles.dropdown_menu}
          onClick={() => {
            navigate("/user/referrals");
            close();
          }}
        >
          {t("inviteFriend")}
        </li>
        <li
          className={styles.dropdown_menu}
          onClick={handleDepositAction}
        >
          {t("deposit")}
        </li>
        <li
          className={styles.dropdown_menu}
          onClick={handleWithdrawalAction}
        >
          {t("withdrawal")}
        </li>
        {/* <li
          className={styles.dropdown_menu}
          onClick={() => {
            handleTransferModal();
            close();
          }}
        >
          {t("transfer")}
        </li> */}
        <li
          className={styles.dropdown_menu}
          onClick={() => {
            handleSupportModal();
            close();
          }}
        >
          {t("support")}
        </li>
        <li
          className={styles.dropdown_menu}
          onClick={close}
        >
          <ThemeToggle />
        </li>
        <li className={styles.dropdown_menu}>
          {/* {t("language")} */}
          <LanguageOption />
        </li>
        <li
          style={{fontSize: "14px", paddingBottom: "2rem"}}
          className={styles.dropdown_menu}
          onClick={() => {
            handleLogout();
            close();
          }}
        >
          <b>{t("logout")}</b>
        </li>
      </ul>
    </div>
  );
};

DropdownRender.propTypes = {
  handleLogout: PropTypes.func.isRequired,
  dropdownStyle: PropTypes.object,
  handleClose: PropTypes.func,
};

export default DropdownRender;
